import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiFetch } from '@/lib/api';

/**
 * The four run-control actions on the production header.
 *
 * All of them change server-side run state, so each reloads or navigates rather than
 * trying to reconcile local state with a run that has just been torn down.
 */
export function useProductionActions(productionId?: string) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);

  const post = useCallback(
    async (action: string, path: string) => {
      if (!productionId || busy) return null;
      setBusy(action);
      try {
        return await apiFetch(path, { method: 'POST' });
      } catch {
        setBusy(null);
        return null;
      }
    },
    [productionId, busy],
  );

  /** Stop the run now. Work already produced is kept. */
  const cancel = useCallback(async () => {
    const r = await post('cancel', `/api/cancel/${productionId}`);
    if (r) window.location.reload();
  }, [post, productionId]);

  /**
   * Keep all pre-production (characters, locations, storyboard, VO, music) and all
   * approvals; wipe and re-run the shoot, edit and delivery.
   */
  const restartShoot = useCallback(async () => {
    const r = await post('restart-shoot', `/api/restart-shoot/${productionId}`);
    if (r) window.location.reload();
  }, [post, productionId]);

  /** Pick the run back up where it stopped. */
  const resume = useCallback(async () => {
    const r = await post('resume', `/api/resume/${productionId}`);
    if (r?.ok) setTimeout(() => window.location.reload(), 1000);
    else setBusy(null);
  }, [post, productionId]);

  /** Start over as a brand-new production. */
  const restart = useCallback(async () => {
    const r = await post('restart', `/api/restart/${productionId}`);
    if (r?.ok) {
      const data = await r.json();
      navigate(`/production/${data.production_id}`);
    }
    setBusy(null);
  }, [post, productionId, navigate]);

  return { busy, cancel, restartShoot, resume, restart };
}
